import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import SlipToggle, { betLegFrom } from "@/components/SlipToggle";
import { useBetSlip } from "@/context/BetSlipContext";
import { useDisplay } from "@/context/DisplayContext";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, ChevronDown, MapPin, CalendarClock, Zap, Target, Star,
  Sparkles, TrendingUp, AlertTriangle, Trophy, ShieldCheck, Gauge, Flame, Swords, Combine, RotateCw, Pin,
  Check, X, Minus, BarChart3,
} from "lucide-react";

// Session cache of fixture analyses so opening a game again (even after navigating away)
// doesn't refetch. A per-card "Reload" button forces a fresh pull.
const analysisCache = new Map();

const fmtDate = (iso) =>
  new Date(iso).toLocaleString("en-AU", {
    weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
  });

const confColor = (c) =>
  c === "High"
    ? "bg-[#34C759]/15 text-[#34C759] border-[#34C759]/30"
    : c === "Medium"
    ? "bg-[#FF9500]/15 text-[#FF9500] border-[#FF9500]/30"
    : "bg-secondary text-muted-foreground border-border";

const pct = (s) => parseInt(String(s || "0").replace("%", ""), 10) || 0;

function LeanChip({ title, lean, odd }) {
  if (!lean) return null;
  return (
    <div className="rounded-2xl bg-secondary/50 p-4" data-testid={`lean-${title.toLowerCase()}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">{title}</div>
        {odd && (
          <span className="text-xs font-bold text-primary bg-primary/10 rounded-full px-2 py-0.5" data-testid="lean-odd">
            @{odd.toFixed(2)}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2 mt-1.5">
        <span className="font-display font-bold text-lg">{lean.pick}</span>
        <Badge variant="outline" className={"rounded-full text-[10px] px-2 " + confColor(lean.confidence)}>
          {lean.confidence}
        </Badge>
      </div>
      <p className="text-xs text-muted-foreground mt-1.5 leading-snug">{lean.reason}</p>
    </div>
  );
}

function ContextTags({ result, home, away }) {
  const ctx = result?.context || {};
  const av = result?.availability || {};
  const tags = [];
  const side = (name, key) => {
    const rest = ctx.rest?.[key];
    if (rest?.days_since != null && rest.days_since <= 3) tags.push({ t: `${name} short rest`, tone: "warn" });
    else if (rest?.games_10d >= 2) tags.push({ t: `${name} busy week`, tone: "warn" });
    const mot = ctx.motivation?.[key];
    if (mot === "high stakes") tags.push({ t: `${name} high stakes`, tone: "hot" });
    else if (mot === "little to play for") tags.push({ t: `${name} low stakes`, tone: "mute" });
    const f = ctx.formation?.[key];
    if (f) tags.push({ t: `${name} ${f}`, tone: "info" });
    const out = av?.[key]?.players_out || [];
    if (out.length) tags.push({ t: `${name}: ${out.length} out`, tone: "warn" });
  };
  side(home, "home");
  side(away, "away");
  if (ctx.referee) tags.push({ t: `Ref: ${ctx.referee}`, tone: "mute" });
  if (!tags.length) return null;
  const tones = {
    warn: "bg-[#FF9500]/15 text-[#FF9500]",
    hot: "bg-[#FF375F]/15 text-[#FF375F]",
    info: "bg-primary/10 text-primary",
    mute: "bg-secondary text-muted-foreground",
  };
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="context-tags">
      {tags.map((tg, i) => (
        <span
          key={i}
          data-testid="context-tag"
          className={"rounded-full px-2.5 py-1 text-[11px] font-semibold " + tones[tg.tone]}
        >
          {tg.t}
        </span>
      ))}
    </div>
  );
}

function MatchPrediction({ result, market, injuries, odds, home, away }) {
  const { fmtProb } = useDisplay();
  if (!result && !market) {
    return <p className="text-sm text-muted-foreground">Prediction not available for this fixture yet.</p>;
  }
  const x = result?.one_x_two || {};
  const o = odds || {};
  const rl = market?.result_lean, gl = market?.goals_lean, bl = market?.btts_lean;
  const tiles = [];
  if (rl) tiles.push({ title: "Result", pick: rl.pick === "Home" ? home : rl.pick === "Away" ? away : "Draw", odd: o[`1x2|${rl.pick}`], conf: rl.confidence });
  if (gl) tiles.push({ title: "Goals", pick: gl.pick, odd: o[`ou|${gl.pick}`], conf: gl.confidence });
  if (bl) tiles.push({ title: "Both Teams to Score", pick: bl.pick, odd: o[`btts|${bl.pick}`], conf: bl.confidence });
  const highConf = result?.confidence === "high";
  const books = result?.odds_source?.books;

  return (
    <div className="glass rounded-3xl p-5 space-y-5" data-testid="result-prediction">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Gauge className="h-4 w-4 text-primary" />
          <span className="font-display font-extrabold">Match Prediction</span>
        </div>
        {result && (
          <div className="flex items-center gap-2">
            {result.stage_label && (
              <Badge
                className="rounded-full text-[10px] border-0 bg-primary/10 text-primary"
                data-testid="stage-badge"
              >
                {result.stage_label}
              </Badge>
            )}
            <Badge
              className="rounded-full text-[10px] border-0"
              style={{ backgroundColor: (highConf ? "#34C759" : "#FF9500") + "22", color: highConf ? "#34C759" : "#FF9500" }}
              data-testid="result-confidence"
            >
              {highConf ? "High confidence" : "Low confidence"}
            </Badge>
          </div>
        )}
      </div>

      {result?.narrative && (
        <div className="rounded-2xl bg-primary/10 px-3.5 py-2.5" data-testid="game-narrative">
          <div className="text-[10px] uppercase tracking-wide text-primary font-semibold mb-0.5">Game narrative</div>
          <p className="text-sm font-medium leading-snug">{result.narrative}</p>
        </div>
      )}

      {result && <ContextTags result={result} home={home} away={away} />}

      {market?.headline && (
        <p className="text-sm text-muted-foreground leading-snug">{market.headline}</p>
      )}

      {result && (
        <>
          <div className="text-center">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Most likely score</div>
            <div className="font-display font-extrabold text-4xl mt-0.5" data-testid="likely-score">
              {result.likely_score || "-"}
            </div>
          </div>

          <div>
            <div className="flex justify-between text-xs font-medium mb-1.5 gap-2">
              <span className="truncate">{home} {fmtProb(x.home)}</span>
              <span className="text-muted-foreground shrink-0">Draw {fmtProb(x.draw)}</span>
              <span className="truncate text-right">{fmtProb(x.away)} {away}</span>
            </div>
            <div className="flex h-3 rounded-full overflow-hidden bg-secondary" data-testid="onextwo-bar">
              <div style={{ width: `${x.home}%`, backgroundColor: "#34C759" }} />
              <div style={{ width: `${x.draw}%`, backgroundColor: "#8E8E93" }} />
              <div style={{ width: `${x.away}%`, backgroundColor: "#0A84FF" }} />
            </div>
          </div>
        </>
      )}

      {tiles.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {tiles.map((t) => (
            <div key={t.title} className="rounded-2xl bg-secondary/50 p-3 text-center" data-testid={`call-${t.title.toLowerCase()}`}>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">{t.title}</div>
              <div className="font-display font-bold text-sm mt-1 truncate" title={t.pick}>{t.pick}</div>
              {t.odd != null ? (
                <span className="text-[11px] font-bold text-primary" data-testid="lean-odd">@{t.odd.toFixed(2)}</span>
              ) : (
                <span className="text-[11px] text-muted-foreground">{t.conf}</span>
              )}
            </div>
          ))}
        </div>
      )}

      {tiles.length > 0 && books > 0 && (
        <p className="text-[11px] text-muted-foreground text-center -mt-2" data-testid="odds-source-note">
          Best price from {books} bookmakers
        </p>
      )}

      {result?.top_scores?.length > 1 && (
        <div data-testid="top-scores">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold mb-2">
            Other likely scorelines
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {result.top_scores.slice(1, 8).map((s, i) => (
              <div
                key={s.score}
                data-testid={`top-score-${i}`}
                className="rounded-xl bg-secondary/50 py-2 text-center"
              >
                <div className="font-display font-bold text-sm">{s.score}</div>
                <div className="text-[11px] text-muted-foreground">{fmtProb(s.prob)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {(market?.key_factors?.length > 0 || injuries?.length > 0) && (
        <div className="border-t border-border pt-3 space-y-3">
          {market?.key_factors?.length > 0 && (
            <details data-testid="why-details">
              <summary className="text-xs font-semibold text-muted-foreground cursor-pointer select-none">Why this call</summary>
              <ul className="space-y-1.5 mt-2">
                {market.key_factors.map((k, i) => (
                  <li key={i} className="text-sm flex items-start gap-2">
                    <TrendingUp className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" /> {k}
                  </li>
                ))}
              </ul>
            </details>
          )}
          {injuries?.length > 0 && (
            <details data-testid="injuries-details">
              <summary className="text-xs font-semibold text-[#FF375F] cursor-pointer select-none flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5" /> Injuries / Doubts ({injuries.length})
              </summary>
              <div className="flex flex-wrap gap-2 mt-2">
                {injuries.map((inj, i) => (
                  <span key={i} className="text-xs bg-secondary rounded-full px-2.5 py-1">
                    {inj.name} · {inj.reason || "out"}
                  </span>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}


function MarketBlock({ fixture, prediction, market, injuries, odds }) {
  const o = odds || {};
  const goalPick = market?.goals_lean?.pick;
  return (
    <div className="space-y-4">
      {market ? (
        <>
          {market.headline && (
            <div className="flex items-start gap-2 rounded-2xl bg-primary/5 border border-primary/15 p-3">
              <Sparkles className="h-4 w-4 text-primary shrink-0 mt-0.5" />
              <p className="text-sm font-medium leading-snug">{market.headline}</p>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <LeanChip title="Result" lean={market.result_lean} odd={o[`1x2|${market.result_lean?.pick}`]} />
            <LeanChip title="Goals" lean={market.goals_lean} odd={o[`ou|${goalPick}`]} />
            <LeanChip title="Both Teams to Score" lean={market.btts_lean} odd={o[`btts|${market.btts_lean?.pick}`]} />
          </div>
          {market.key_factors?.length > 0 && (
            <div className="rounded-2xl bg-secondary/40 p-4">
              <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-2">
                Key Factors
              </div>
              <ul className="space-y-1.5">
                {market.key_factors.map((k, i) => (
                  <li key={i} className="text-sm flex items-start gap-2">
                    <TrendingUp className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                    {k}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      ) : (
        <p className="text-sm text-muted-foreground">Game-market prediction is not available for this fixture yet.</p>
      )}

      {injuries?.length > 0 && (
        <div className="rounded-2xl bg-[#FF375F]/5 border border-[#FF375F]/15 p-4">
          <div className="text-xs uppercase tracking-wide text-[#FF375F] font-semibold mb-2 flex items-center gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5" /> Injuries / Doubts
          </div>
          <div className="flex flex-wrap gap-2">
            {injuries.map((inj, i) => (
              <span key={i} className="text-xs bg-secondary rounded-full px-2.5 py-1">
                {inj.name} · {inj.reason || "out"}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AvailBadge({ avail }) {
  if (!avail) return null;
  if (avail.confirmed) {
    return (
      <span className="flex items-center gap-1 text-[#34C759] font-medium" data-testid="avail-confirmed">
        <ShieldCheck className="h-3 w-3" /> Confirmed to start
      </span>
    );
  }
  if (avail.rotation_risk) {
    return (
      <span className="flex items-center gap-1 text-[#FF9500] font-medium" data-testid="avail-rotation">
        <AlertTriangle className="h-3 w-3" /> Might be rested · {avail.start_prob}% to start
      </span>
    );
  }
  if (avail.start_prob != null) {
    return (
      <span className="flex items-center gap-1" data-testid="avail-start">
        <ShieldCheck className="h-3 w-3 text-[#0A84FF]" /> {avail.start_prob}% likely to start
      </span>
    );
  }
  return null;
}

function HitCard({ c, fixtureCtx }) {
  const { fmtProb } = useDisplay();
  const leg = fixtureCtx ? betLegFrom({ ...c, ...fixtureCtx }) : null;
  return (
    <div className="rounded-2xl bg-secondary/50 p-3.5" data-testid="hit-card">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          {c.photo && <img src={c.photo} alt="" className="h-9 w-9 rounded-full object-cover shrink-0" />}
          <div className="min-w-0">
            <div className="font-semibold text-sm truncate">{c.player}</div>
            <div className="text-xs text-muted-foreground">{c.prop}</div>
          </div>
        </div>
        <div className="flex flex-col items-end shrink-0">
          <div
            className={
              "font-display font-extrabold text-lg leading-none " +
              (c.recent.pct === 100 ? "text-[#34C759]" : "text-foreground")
            }
          >
            {fmtProb(c.recent.pct)}
          </div>
          <div className="text-[9px] uppercase tracking-wide text-muted-foreground mt-0.5">chance to hit</div>
          {c.odd != null && (
            <span className="text-xs font-bold text-primary bg-primary/10 rounded-full px-2 py-0.5 mt-0.5" data-testid="prop-odd">
              @{c.odd.toFixed(2)}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-end justify-between gap-2 mt-2.5">
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <Zap className="h-3 w-3 text-[#FF9500]" />
            {c.recent.club_form
              ? `about ${c.recent.hits} in ${c.recent.total} club games`
              : `Hit ${c.recent.hits} of last ${c.recent.total}`}
          </span>
          {c.vs_opp && (
            <span className="flex items-center gap-1">
              <Target className="h-3 w-3 text-[#0A84FF]" /> {c.vs_opp.hits} of {c.vs_opp.total} vs this opponent
            </span>
          )}
          {c.starter && (
            <span className="flex items-center gap-1">
              <Star className="h-3 w-3 text-[#AF52DE]" /> {c.starter.hits} of {c.starter.total} when starting
            </span>
          )}
          <AvailBadge avail={c.avail} />
        </div>
        <SlipToggle leg={leg} />
      </div>
    </div>
  );
}

const RISK = [
  { key: "low_risk", label: "Low Risk", dot: "#34C759", icon: ShieldCheck,
    hint: "Most consistent, safest picks" },
  { key: "medium_risk", label: "Medium Risk", dot: "#FF9500", icon: Gauge,
    hint: "Solid, but not a lock" },
  { key: "high_risk", label: "High Risk", dot: "#FF375F", icon: Flame,
    hint: "Long shots, bigger payout, less likely" },
];

function TeamCheats({ label, logo, cheats, fixtureCtx }) {
  if (!cheats) return null;
  const hasAny = RISK.some((r) => (cheats[r.key] || []).length);
  return (
    <div className="space-y-4" data-testid="team-cheats">
      <div className="flex items-center gap-2">
        {logo && <img src={logo} alt="" className="h-6 w-6 object-contain" />}
        <h4 className="font-display font-bold">{label}</h4>
      </div>
      {!hasAny ? (
        <p className="text-xs text-muted-foreground">No prop data available for this team.</p>
      ) : (
        RISK.map((r) => {
          const cards = cheats[r.key] || [];
          if (!cards.length) return null;
          const Icon = r.icon;
          return (
            <div key={r.key} className="space-y-2" data-testid={`risk-group-${r.key}`}>
              <div className="flex items-center gap-1.5">
                <Icon className="h-4 w-4" style={{ color: r.dot }} />
                <span className="font-semibold text-sm" style={{ color: r.dot }}>{r.label}</span>
                <span className="text-[11px] text-muted-foreground">· {r.hint}</span>
              </div>
              {cards.map((c, i) => (
                <HitCard key={`${r.key}-${i}`} c={c} fixtureCtx={fixtureCtx} />
              ))}
            </div>
          );
        })
      )}
    </div>
  );
}

function HeadToHead({ h2h, home, away }) {
  if (!h2h || h2h.length === 0) return null;
  return (
    <section data-testid="h2h-section">
      <h3 className="font-display font-extrabold text-lg mb-3 flex items-center gap-2">
        <Swords className="h-4 w-4 text-primary" /> Head-to-Head
      </h3>
      <div className="space-y-2">
        {h2h.map((m, i) => {
          const hs = m.home_goals, as = m.away_goals;
          return (
            <div
              key={i}
              className="rounded-2xl bg-secondary/50 p-3 flex items-center gap-3"
              data-testid="h2h-row"
            >
              <span className="text-xs text-muted-foreground w-20 shrink-0">
                {new Date(m.date).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "2-digit" })}
              </span>
              <div className="flex items-center gap-2 flex-1 justify-end min-w-0">
                <span className={"text-sm truncate text-right " + (m.home_winner ? "font-bold" : "")}>
                  {m.home}
                </span>
                {m.home_logo && <img src={m.home_logo} alt="" className="h-5 w-5 object-contain shrink-0" />}
              </div>
              <span className="font-display font-extrabold text-sm bg-card rounded-lg px-2.5 py-1 shrink-0">
                {hs}-{as}
              </span>
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {m.away_logo && <img src={m.away_logo} alt="" className="h-5 w-5 object-contain shrink-0" />}
                <span className={"text-sm truncate " + (m.away_winner ? "font-bold" : "")}>{m.away}</span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

const MULTI_META = {
  Safe: { color: "#34C759", icon: ShieldCheck },
  Balanced: { color: "#FF9500", icon: Gauge },
  Longshot: { color: "#FF375F", icon: Flame },
};

function MultiCard({ multi, ctx }) {
  const { fmtProb } = useDisplay();
  const { toggleLeg, has } = useBetSlip();
  const meta = MULTI_META[multi.type] || MULTI_META.Balanced;
  const Icon = meta.icon;

  const slipLegs = multi.legs.map((l) => ({
    id: `${ctx.fixture_id}|${l.prop_key}|${l.player}`,
    player: l.player, prop: l.prop, odd: l.odd, prob: l.prob,
    home: ctx.home, away: ctx.away, tournament: ctx.tournament, date: ctx.date,
  }));
  const allIn = slipLegs.every((l) => has(l.id));

  const load = () => {
    slipLegs.forEach((l) => {
      if (allIn || !has(l.id)) toggleLeg(l);
    });
    toast.success(allIn ? "Removed multi from slip" : `${multi.size}-leg multi added to slip`);
  };

  return (
    <div className="rounded-2xl border border-border p-4" data-testid="multi-card">
      <div className="flex items-center justify-between gap-3">
        <span
          className="inline-flex items-center gap-1 text-xs font-bold rounded-full px-2.5 py-1"
          style={{ color: meta.color, backgroundColor: meta.color + "22" }}
        >
          <Icon className="h-3.5 w-3.5" /> {multi.type} · {multi.size} legs
        </span>
        <div className="text-right">
          <div className="font-display font-extrabold text-xl">@{multi.combined_odds.toFixed(2)}</div>
        </div>
      </div>

      <div className="mt-3 space-y-1.5">
        {multi.legs.map((l, i) => (
          <div key={i} className="flex items-center justify-between text-sm">
            <span className="truncate mr-2">
              <span className="font-medium">{l.player}</span>
              <span className="text-muted-foreground"> · {l.prop}</span>
            </span>
            <span className="text-xs text-primary font-semibold shrink-0">@{l.odd.toFixed(2)}</span>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-2 mt-3">
        <div className="rounded-xl bg-secondary/50 py-2 text-center" title="Our estimated chance all legs land">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Model chance</div>
          <div className="font-bold text-sm">{fmtProb(multi.chance)}</div>
        </div>
        <div className="rounded-xl py-2 text-center" title="How the odds compare to our model chance. Positive means good value." style={{ backgroundColor: (multi.value_pct >= 0 ? "#34C759" : "#FF375F") + "1a" }}>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Value</div>
          <div className="font-bold text-sm" style={{ color: multi.value_pct >= 0 ? "#34C759" : "#FF375F" }}>
            {multi.value_pct >= 0 ? "+" : ""}{multi.value_pct}%
          </div>
        </div>
      </div>

      <Button
        onClick={load}
        variant={allIn ? "secondary" : "default"}
        data-testid="load-multi-to-slip"
        className="w-full rounded-xl mt-3 h-9 text-sm font-semibold"
      >
        <Combine className="h-4 w-4" /> {allIn ? "In slip, tap to remove" : "Load multi to slip"}
      </Button>
    </div>
  );
}

function SuggestedMultis({ multis, ctx }) {
  if (!multis || multis.length === 0) return null;
  return (
    <section data-testid="suggested-multis">
      <h3 className="font-display font-extrabold text-lg mb-1 flex items-center gap-2">
        <Combine className="h-4 w-4 text-primary" /> Suggested Multis
      </h3>
      <p className="text-xs text-muted-foreground mb-3">
        Auto-built combinations, bigger payouts, lower combined chance. Model chance assumes legs are independent.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {multis.map((m, i) => (
          <MultiCard key={i} multi={m} ctx={ctx} />
        ))}
      </div>
    </section>
  );
}


const aiPredCache = new Map();

const GradeIcon = ({ r }) =>
  r === "hit" ? <Check className="h-4 w-4 text-[#34C759]" data-testid="grade-hit" /> :
  r === "miss" ? <X className="h-4 w-4 text-[#FF3B30]" data-testid="grade-miss" /> :
  r === "na" ? <Minus className="h-4 w-4 text-muted-foreground" data-testid="grade-na" /> : null;

function AIPropGroup({ title, icon: Icon, items, teamName, graded }) {
  if (!items || !items.length) return null;
  const gmap = {};
  (graded || []).forEach((g) => { gmap[g.player] = g; });
  return (
    <div className="rounded-2xl bg-secondary/30 p-4" data-testid="ai-prop-group">
      <p className="font-display font-bold text-sm mb-2 flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5 text-primary" /> {title}
      </p>
      <div className="space-y-1.5">
        {items.map((p, i) => {
          const g = gmap[p.player];
          return (
            <div key={i} className="flex items-start justify-between gap-3 py-1.5 border-b border-border/40 last:border-0"
                 data-testid="ai-prop-row">
              <div className="min-w-0">
                <p className="font-semibold text-sm truncate">
                  {p.player}
                  <span className="text-[10px] font-medium text-muted-foreground ml-1.5">{teamName(p.team)}</span>
                </p>
                {p.reasoning && <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{p.reasoning}</p>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {p.pick && (
                  <span className={"text-xs font-bold rounded-full px-2.5 py-0.5 border " +
                    (p.pick === "Over"
                      ? "bg-[#34C759]/15 text-[#34C759] border-[#34C759]/30"
                      : "bg-[#FF3B30]/10 text-[#FF3B30] border-[#FF3B30]/25")}>
                    {p.pick} {p.line}
                  </span>
                )}
                {g && (
                  <span className="flex items-center gap-1" title={`Actual: ${g.actual ?? "did not play"}`}>
                    {g.actual != null && <span className="text-[10px] text-muted-foreground">{g.actual}</span>}
                    <GradeIcon r={g.result} />
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AIPrediction({ fixtureId, home, away }) {
  const [data, setData] = useState(() => aiPredCache.get(fixtureId) || null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(false);
  const [acc, setAcc] = useState(null);

  const load = async (force = false) => {
    setLoading(true); setErr(false);
    try {
      const r = await api.get(`/tournaments/ai-prediction/${fixtureId}`,
        force ? { params: { refresh: 1 } } : undefined);
      aiPredCache.set(fixtureId, r.data);
      setData(r.data);
    } catch (e) {
      setErr(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!data) load();
    api.get("/ai-accuracy").then((r) => setAcc(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const teamName = (t) => (t === "home" ? home : t === "away" ? away : "");
  const resultLabel = (r) => (r === "Home" ? home : r === "Away" ? away : "Draw");
  const p = data?.prediction;
  const m = p?.match;
  const grade = data?.grade;
  const gp = grade?.props || {};

  return (
    <section data-testid="ai-prediction" className="rounded-3xl border border-primary/25 bg-primary/[0.04] p-5">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <h3 className="font-display font-extrabold text-lg flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" /> Pitchie AI · Prediction
        </h3>
        {acc && acc.games > 0 && (
          <span className="text-[11px] text-muted-foreground flex items-center gap-2 rounded-full bg-secondary/60 px-3 py-1"
                data-testid="ai-track-record">
            <BarChart3 className="h-3.5 w-3.5 text-primary" />
            Track record: {acc.games} game{acc.games === 1 ? "" : "s"}
            {acc.result_pct != null && <> · Results <b className="text-foreground">{acc.result_pct}%</b></>}
            {acc.props?.pct != null && <> · Props <b className="text-foreground">{acc.props.pct}%</b></>}
          </span>
        )}
      </div>

      {loading ? (
        <div className="py-8 flex flex-col items-center gap-2 text-center">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          <p className="text-xs text-muted-foreground">Pitchie AI is thinking, this takes ~15-20s.</p>
        </div>
      ) : err ? (
        <div className="py-6 text-center space-y-2">
          <p className="text-sm text-muted-foreground">Couldn't get a prediction.</p>
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => load(false)}
                  data-testid="ai-prediction-retry">Try again</Button>
        </div>
      ) : m ? (
        <div className="space-y-4">
          <div className="rounded-2xl bg-background/60 p-4 flex flex-wrap items-center gap-3" data-testid="ai-match-pred">
            <span className="text-xs font-bold rounded-full px-3 py-1 bg-primary/15 text-primary">
              {resultLabel(m.result)}
            </span>
            {m.score && <span className="font-display font-extrabold text-2xl">{m.score}</span>}
            {m.confidence && (
              <span className={"text-xs font-semibold rounded-full px-2.5 py-1 border " + confColor(m.confidence)}>
                {m.confidence} confidence
              </span>
            )}
            {grade && (
              <span className={"flex items-center gap-1.5 text-xs font-semibold rounded-full px-2.5 py-1 " +
                (grade.match.result_correct ? "bg-[#34C759]/15 text-[#34C759]" : "bg-[#FF3B30]/10 text-[#FF3B30]")}
                    data-testid="ai-match-grade">
                <GradeIcon r={grade.match.result_correct ? "hit" : "miss"} />
                Actual {grade.match.actual_score} ({resultLabel(grade.match.actual_result)})
              </span>
            )}
            {m.reasoning && <p className="text-xs text-muted-foreground w-full mt-1">{m.reasoning}</p>}
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <AIPropGroup title="Goal Scorers" icon={Target} items={p.props?.goal_scorers} teamName={teamName} graded={gp.goal_scorers} />
            <AIPropGroup title="Shots on Target" icon={Zap} items={p.props?.shots_on_target} teamName={teamName} graded={gp.shots_on_target} />
            <AIPropGroup title="Shots" icon={Flame} items={p.props?.shots} teamName={teamName} graded={gp.shots} />
            <AIPropGroup title="Tackles" icon={ShieldCheck} items={p.props?.tackles} teamName={teamName} graded={gp.tackles} />
          </div>
          <div className="flex items-center justify-between gap-2 pt-1">
            <span className="text-[11px] text-muted-foreground">
              {grade ? "Graded after full time." : "Regrades automatically after full time."}
            </span>
            <Button variant="ghost" size="sm" onClick={() => load(true)} data-testid="ai-prediction-reload"
                    className="rounded-full text-xs gap-1.5 text-muted-foreground hover:text-foreground shrink-0">
              <RotateCw className="h-3.5 w-3.5" /> Regenerate
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function FixtureCard({ fixture, index }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(() => analysisCache.get(fixture.id) || null);
  const [loading, setLoading] = useState(false);

  const fetchAnalysis = async (force = false) => {
    setLoading(true);
    try {
      const r = await api.get(`/tournaments/analysis/${fixture.id}`,
        force ? { params: { refresh: 1 } } : undefined);
      analysisCache.set(fixture.id, r.data);
      setData(r.data);
      return true;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      return false;
    } finally {
      setLoading(false);
    }
  };

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !data && !loading) {
      const ok = await fetchAnalysis(false);
      if (!ok) setOpen(false);
    }
  };

  const reload = async (e) => {
    e.stopPropagation();
    await fetchAnalysis(true);
    toast.success("Stats refreshed");
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      className="glass rounded-3xl overflow-hidden"
      data-testid="fixture-card"
    >
      <button onClick={toggle} data-testid="fixture-toggle"
              className="w-full p-5 text-left cursor-pointer hover:bg-secondary/20 transition-colors group">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 flex-1 justify-end min-w-0">
            <span className="font-display font-bold truncate text-right">{fixture.home.name}</span>
            {fixture.home.logo && <img src={fixture.home.logo} alt="" className="h-8 w-8 object-contain shrink-0" />}
          </div>
          <span className="text-xs font-bold text-muted-foreground px-2 shrink-0">VS</span>
          <div className="flex items-center gap-2.5 flex-1 min-w-0">
            {fixture.away.logo && <img src={fixture.away.logo} alt="" className="h-8 w-8 object-contain shrink-0" />}
            <span className="font-display font-bold truncate">{fixture.away.name}</span>
          </div>
          <span className={
            "flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-1 shrink-0 transition-colors " +
            (open ? "bg-secondary text-foreground" : "bg-primary/15 text-primary group-hover:bg-primary/25")
          }>
            {open ? "Close" : "Predict"}
            <ChevronDown className={"h-3.5 w-3.5 transition-transform " + (open ? "rotate-180" : "")} />
          </span>
        </div>
        <div className="flex items-center justify-center gap-4 mt-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <CalendarClock className="h-3.5 w-3.5" /> {fmtDate(fixture.date)}
          </span>
          {fixture.venue?.name && (
            <span className="flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5" /> {fixture.venue.name}
              {fixture.venue.city ? `, ${fixture.venue.city}` : ""}
            </span>
          )}
          {fixture.league?.round && <span className="hidden sm:inline">{fixture.league.round}</span>}
        </div>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="border-t border-border"
          >
            {loading ? (
              <div className="p-10 flex flex-col items-center justify-center gap-2 text-center">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
                <p className="text-xs text-muted-foreground">
                  Crunching stats, this can take ~15s the first time.
                </p>
              </div>
            ) : data ? (
              <div className="p-5 space-y-8" data-testid="fixture-analysis">
                <div className="flex items-center justify-between gap-2 -mb-3">
                  <span className="text-[11px] text-muted-foreground">
                    Stats cached, reopen instantly. Tap reload for fresh odds & lineups.
                  </span>
                  <Button
                    variant="ghost" size="sm" onClick={reload}
                    data-testid="reload-stats-btn"
                    className="rounded-full text-xs gap-1.5 text-muted-foreground hover:text-foreground shrink-0"
                  >
                    <RotateCw className="h-3.5 w-3.5" /> Reload
                  </Button>
                </div>
                <AIPrediction
                  fixtureId={data.fixture.id}
                  home={data.fixture.home.name}
                  away={data.fixture.away.name}
                />
                <section>
                  <MatchPrediction
                    result={data.result}
                    market={data.market}
                    injuries={data.injuries}
                    odds={data.odds}
                    home={data.fixture.home.name}
                    away={data.fixture.away.name}
                  />
                </section>
                <SuggestedMultis
                  multis={data.multis}
                  ctx={{
                    fixture_id: data.fixture.id,
                    home: data.fixture.home.name,
                    away: data.fixture.away.name,
                    date: data.fixture.date,
                    tournament: data.fixture.league?.name,
                  }}
                />
                <HeadToHead h2h={data.h2h} home={data.fixture.home.name} away={data.fixture.away.name} />
                <section>
                  <h3 className="font-display font-extrabold text-lg mb-1 flex items-center gap-2 flex-wrap">
                    <Trophy className="h-4 w-4 text-primary" /> Player Prop Bets
                    {(() => {
                      const lc = data.cheatsheets?.lineups_confirmed;
                      const confirmed = lc && (lc.home || lc.away);
                      return (
                        <span
                          className={`text-[10px] font-semibold rounded-full px-2 py-0.5 border-0 ${confirmed ? "bg-[#34C759]/15 text-[#34C759]" : "bg-secondary text-muted-foreground"}`}
                          data-testid="lineup-status"
                        >
                          {confirmed ? "Confirmed lineup ✓" : "Projected lineup (from recent form)"}
                        </span>
                      );
                    })()}
                  </h3>
                  <p className="text-xs text-muted-foreground mb-3 max-w-2xl">
                    The big number is our estimated chance the player hits that line. Cards are grouped
                    Low to High risk, and @odds show the best price we found. Tap the + to add one to your slip.
                  </p>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <TeamCheats
                      label={data.fixture.home.name}
                      logo={data.fixture.home.logo}
                      cheats={data.cheatsheets.home}
                      fixtureCtx={{
                        fixture_id: data.fixture.id,
                        home: data.fixture.home.name,
                        away: data.fixture.away.name,
                        date: data.fixture.date,
                        tournament: data.fixture.league?.name,
                      }}
                    />
                    <TeamCheats
                      label={data.fixture.away.name}
                      logo={data.fixture.away.logo}
                      cheats={data.cheatsheets.away}
                      fixtureCtx={{
                        fixture_id: data.fixture.id,
                        home: data.fixture.home.name,
                        away: data.fixture.away.name,
                        date: data.fixture.date,
                        tournament: data.fixture.league?.name,
                      }}
                    />
                  </div>
                </section>
              </div>
            ) : null}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function TournamentDetail() {
  const { name } = useParams();
  const navigate = useNavigate();
  const [league, setLeague] = useState(null);
  const [fixtures, setFixtures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pinned, setPinned] = useState(false);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/tournaments/${encodeURIComponent(name)}/fixtures`)
      .then((r) => {
        setLeague(r.data.league);
        setFixtures(r.data.fixtures);
      })
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
    api.get("/me/pins").then((r) => setPinned((r.data.pinned || []).includes(name))).catch(() => {});
  }, [name]);

  const togglePin = async () => {
    try {
      const { data } = pinned
        ? await api.delete(`/me/pins/${encodeURIComponent(name)}`)
        : await api.post(`/me/pins/${encodeURIComponent(name)}`);
      const now = (data.pinned || []).includes(name);
      setPinned(now);
      toast.success(now ? `Pinned ${name} to Home` : `Unpinned ${name}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="tournament-detail-page">
      <button
        onClick={() => navigate("/tournaments")}
        data-testid="back-to-tournaments"
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-4 w-4" /> Competitions
      </button>

      <div className="flex items-center gap-3">
        {league?.logo && (
          <div className="h-14 w-14 rounded-2xl bg-secondary/60 flex items-center justify-center shrink-0">
            <img src={league.logo} alt="" className="h-9 w-9 object-contain" />
          </div>
        )}
        <div className="min-w-0">
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">{name}</h1>
          {league && (
            <p className="text-muted-foreground text-sm mt-0.5 flex items-center gap-1.5">
              {league.flag && <img src={league.flag} alt="" className="h-3 w-4 object-cover rounded-sm" />}
              {league.country || "World"} · Season {league.season} · Next {fixtures.length} fixtures
            </p>
          )}
        </div>
        <button
          onClick={togglePin}
          data-testid="detail-pin-btn"
          title={pinned ? "Unpin from Home" : "Pin to Home"}
          className={
            "ml-auto shrink-0 flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold transition-colors " +
            (pinned ? "bg-primary/15 text-primary" : "bg-secondary/60 text-muted-foreground hover:text-foreground")
          }
        >
          <Pin className={"h-4 w-4 " + (pinned ? "fill-current" : "")} />
          <span className="hidden sm:inline">{pinned ? "Pinned" : "Pin"}</span>
        </button>
      </div>

      {loading ? (
        <div className="h-40 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : error ? (
        <div className="glass rounded-3xl p-12 text-center" data-testid="tournament-error">
          <AlertTriangle className="h-10 w-10 mx-auto text-muted-foreground mb-4" />
          <h3 className="font-display text-xl font-bold">Couldn't load fixtures</h3>
          <p className="text-muted-foreground text-sm mt-1">{error}</p>
        </div>
      ) : fixtures.length === 0 ? (
        <div className="glass rounded-3xl p-12 text-center" data-testid="no-fixtures">
          <CalendarClock className="h-10 w-10 mx-auto text-muted-foreground mb-4" />
          <h3 className="font-display text-xl font-bold">No upcoming fixtures</h3>
          <p className="text-muted-foreground text-sm mt-1">
            There are no scheduled matches for this competition right now.
          </p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="fixtures-list">
          {fixtures.map((f, i) => (
            <FixtureCard key={f.id} fixture={f} index={i} />
          ))}
        </div>
      )}
    </div>
  );
}
